import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { startRegistration } from "@simplewebauthn/browser";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import BiometricsCard from "@/components/BiometricsCard";
import { Settings, KeyRound, Fingerprint, Palette, Clock, Trash2, Plus, Sun, Moon, Monitor, Check } from "lucide-react";

const ACCENTS = [
  { value: "navy", label: "Navy", color: "#001A4B" },
  { value: "crimson", label: "Crimson", color: "#8c1d2c" },
  { value: "forest", label: "Forest", color: "#14532d" },
  { value: "ocean", label: "Ocean", color: "#075985" },
];
const THEMES = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

export default function AccountSettings() {
  const { theme, accent, setTheme, setAccent } = useTheme();
  const { user } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters.");
    if (pw.new_password !== pw.confirm) return toast.error("New passwords do not match.");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  return (
    <div data-testid="account-settings-page" className="max-w-3xl">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Your account</div>
        <h1 className="font-serif text-4xl tracking-tight flex items-center gap-3 mt-1"><Settings className="h-8 w-8 text-primary" /> Settings</h1>
        <p className="text-muted-foreground text-sm mt-2">Signed in as <span className="text-foreground font-medium">{user?.email}</span></p>
      </div>

      {/* Password */}
      <div className="bg-card border border-border rounded-lg p-6 mb-5">
        <h2 className="font-serif text-xl mb-1 flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> Change password</h2>
        <form onSubmit={changePw} className="mt-3 space-y-3 max-w-md">
          <input data-testid="current-password" type="password" required placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="ud-input" />
          <input data-testid="new-password" type="password" required placeholder="New password (min 8 chars)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="ud-input" />
          <input data-testid="confirm-password" type="password" required placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="ud-input" />
          <button data-testid="save-password" type="submit" disabled={savingPw} className="px-4 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{savingPw ? "Saving…" : "Update password"}</button>
        </form>
      </div>

      {/* Biometrics / passkeys */}
      <BiometricsCard />

      {/* Appearance */}
      <div className="bg-card border border-border rounded-lg p-6 mb-5">
        <h2 className="font-serif text-xl mb-1 flex items-center gap-2"><Palette className="h-5 w-5 text-primary" /> Appearance</h2>
        <p className="text-sm text-muted-foreground mb-4">Choose a color scheme and accent. Your choice follows you across devices.</p>
        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Mode</div>
        <div className="flex gap-2 mb-6 flex-wrap">
          {THEMES.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.value} data-testid={`theme-${t.value}`} onClick={() => setTheme(t.value)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-md border text-sm transition-colors ${theme === t.value ? "border-primary bg-primary/10 text-primary font-medium" : "border-border hover:bg-muted"}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>
        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Accent</div>
        <div className="flex gap-3 flex-wrap">
          {ACCENTS.map((a) => (
            <button key={a.value} data-testid={`accent-${a.value}`} onClick={() => setAccent(a.value)} title={a.label}
              className={`relative h-11 w-11 rounded-full border-2 transition-transform hover:scale-105 ${accent === a.value ? "border-foreground" : "border-transparent"}`}
              style={{ backgroundColor: a.color }}>
              {accent === a.value && <Check className="h-5 w-5 text-white absolute inset-0 m-auto" />}
            </button>
          ))}
        </div>
      </div>

      {/* Session */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="font-serif text-xl mb-1 flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> Session security</h2>
        <p className="text-sm text-muted-foreground">For your protection, you'll be automatically signed out after <b>30 minutes</b> of inactivity. Any activity resets the timer.</p>
      </div>
    </div>
  );
}
